if (!customElements.get('top-lash-order')) {
  customElements.define('top-lash-order', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return
      try {
        this.data = JSON.parse(this.querySelector('[data-order]').textContent)
        this.text = this.data.text
        if (this.data.colorOrder) Object.assign(this.text, this.data.colorText)
        if (this.data.genericOrder) Object.assign(this.text, this.data.genericText)
        this.variants = this.data.variants
        this.options = this.data.options
        if (!this.variants.length) return
        this.axis = this.options.findIndex(o => (this.data.colorOrder ? /color|colour|^tono$|^shade$/i : /^(longitud|largura|length)$/i).test(o.name.trim()) && o.values.length > 1)
        if (this.axis < 0) this.axis = this.options.findIndex(o => o.values.length > 1)
        if (this.axis < 0) return
        this.chosen = new Map()
        const initial = this.variants.find(v => v.id === this.data.selected) || this.variants[0]
        this.filters = [...initial.options]
        this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'es', { style: 'currency', currency: this.data.currency })
        this.addButton = this.querySelector('[data-add]')
        this.status = this.querySelector('[data-status]')
        this.rows = this.querySelector('[data-rows]')
        this.renderFilters()
        this.renderRows()
        this.addEventListener('click', e => this.handleClick(e))
        const quantityChanged = e => {
          if (!e.target.matches('[data-quantity]')) return
          const variant = this.byId(e.target.dataset.quantity)
          const raw = Number(e.target.value)
          const next = this.normalize(variant, Number.isFinite(raw) ? raw : 0)
          this.setQuantity(variant, next)
          if (raw !== next) this.status.textContent = this.text.adjusted
        }
        this.addEventListener('input', quantityChanged)
        this.addEventListener('change', quantityChanged)
        this.ready = true
        this.hidden = false
      } catch (error) {
        this.hidden = true
      }
    }

    byId(id) { return this.variants.find(v => String(v.id) === String(id)) }
    money(cents) { return this.formatter.format(cents / 100) }
    fill(text, values) { return text.replace(/\{(\w+)\}/g, (_, key) => values[key] ?? '') }
    limit(v) { return Math.min(v.max ?? Infinity, v.stock ?? Infinity) }
    normalize(v, amount) {
      if (!v.available || amount <= 0) return 0
      const min = Math.max(1, v.min), step = Math.max(1, v.step)
      const limit = this.limit(v)
      if (limit < min) return 0
      const bounded = Math.min(Math.max(min, Math.floor(amount)), limit)
      return min + Math.floor((bounded - min) / step) * step
    }
    element(tag, className, text) {
      const node = document.createElement(tag)
      if (className) node.className = className
      if (text !== undefined) node.textContent = text
      return node
    }
    button(className, text) {
      const node = this.element('button', className, text)
      node.type = 'button'
      return node
    }

    renderFilters() {
      const host = this.querySelector('[data-filters]')
      host.replaceChildren()
      this.options.forEach((option, index) => {
        if (index === this.axis) return
        const group = this.element('fieldset')
        group.append(this.element('legend', '', option.name))
        const choices = this.element('div', 'tlo-choices')
        option.values.forEach(value => {
          const button = this.button('tlo-choice', value)
          button.dataset.option = index
          button.dataset.value = value
          button.setAttribute('aria-pressed', String(this.filters[index] === value))
          choices.append(button)
        })
        group.append(choices)
        host.append(group)
      })
      this.querySelector('[data-axis]').textContent = this.options[this.axis].name
    }

    renderRows() {
      this.rows.replaceChildren()
      this.options[this.axis].values.forEach(value => {
        const variant = this.variants.find(v => v.options.every((part, index) => part === (index === this.axis ? value : this.filters[index])))
        const row = this.element('div', 'tlo-row')
        const description = this.element('div', 'tlo-description')
        if (this.data.colorOrder) {
          const shade = this.data.swatches?.[this.axis]?.find(s => s.name === value)
          if (shade?.color || shade?.image) {
            const dot = this.element('span', 'tlo-color-dot')
            dot.setAttribute('aria-hidden', 'true')
            if (shade.color) dot.style.background = shade.color
            if (shade.image) { dot.style.backgroundImage = `url(${JSON.stringify(shade.image)})`; dot.style.backgroundSize = 'cover' }
            description.append(dot)
          }
        }
        description.append(this.element('strong', '', value))
        if (variant) description.append(this.element('span', 'tlo-price', this.money(variant.price)))
        row.append(description)
        if (!variant || !variant.available || this.limit(variant) < variant.min) {
          row.dataset.unavailable = ''
          const notice = this.element('div', 'tlo-stock-notice')
          notice.append(this.element('span', 'tlo-unavailable', variant ? this.text.soldOut : this.text.unavailable))
          if (variant && !variant.available) {
            const notify = this.button('tlo-restock', this.text.notify)
            notify.addEventListener('click', () => this.dispatchEvent(new CustomEvent('top:restock', {bubbles:true, detail:{id:variant.id}})))
            notice.append(notify)
          }
          row.append(notice)
        } else {
          const control = this.element('div', 'tlo-stepper')
          const minus = this.button('', '−')
          minus.dataset.minus = variant.id
          minus.setAttribute('aria-label', `${this.text.less}: ${variant.options.join(' · ')}`)
          const input = this.element('input')
          input.type = 'number'
          input.inputMode = 'numeric'
          input.min = '0'
          input.step = '1'
          if (Number.isFinite(this.limit(variant))) input.max = this.limit(variant)
          input.value = this.chosen.get(variant.id) || 0
          input.dataset.quantity = variant.id
          input.setAttribute('aria-label', `${this.text.quantity}: ${variant.options.join(' · ')}`)
          const plus = this.button('', '+')
          plus.dataset.plus = variant.id
          plus.setAttribute('aria-label', `${this.text.more}: ${variant.options.join(' · ')}`)
          control.append(minus, input, plus)
          row.append(control)
        }
        this.rows.append(row)
      })
      this.update()
    }

    handleClick(event) {
      const button = event.target.closest('button')
      if (!button || !this.contains(button) || this.busy) return
      if (button.hasAttribute('data-option')) {
        const index = Number(button.dataset.option)
        this.filters[index] = button.dataset.value
        this.querySelectorAll(`[data-option="${index}"]`).forEach(el => el.setAttribute('aria-pressed', String(el.dataset.value === this.filters[index])))
        this.renderRows()
      } else if (button.hasAttribute('data-plus') || button.hasAttribute('data-minus')) {
        const variant = this.byId(button.dataset.plus || button.dataset.minus)
        const current = this.chosen.get(variant.id) || 0
        const amount = button.hasAttribute('data-plus') ? (current ? current + variant.step : variant.min) : (current <= variant.min ? 0 : current - variant.step)
        this.setQuantity(variant, this.normalize(variant, amount))
      } else if (button.hasAttribute('data-remove')) {
        this.setQuantity(this.byId(button.dataset.remove), 0)
      } else if (button.hasAttribute('data-clear')) {
        this.chosen.clear()
        this.update()
        this.status.textContent = ''
      } else if (button.hasAttribute('data-add')) {
        this.addToCart()
      }
    }

    setQuantity(variant, quantity) {
      if (quantity) this.chosen.set(variant.id, quantity)
      else this.chosen.delete(variant.id)
      if (!this.uncertain) this.status.textContent = ''
      this.update()
    }

    update() {
      let count = 0, total = 0
      const list = this.querySelector('[data-selected-list]')
      const focusedRemove = document.activeElement?.dataset.remove
      list.replaceChildren()
      this.chosen.forEach((quantity, id) => {
        const variant = this.byId(id)
        count += quantity
        total += quantity * variant.price
        const item = this.element('li')
        item.append(this.element('span', '', `${variant.options.join(' · ')} × ${quantity}`))
        const remove = this.button('', '×')
        remove.dataset.remove = id
        remove.setAttribute('aria-label', `${this.text.remove}: ${variant.options.join(' · ')}`)
        item.append(remove)
        list.append(item)
      })
      if (focusedRemove) (list.querySelector(`[data-remove="${focusedRemove}"]`) || this.querySelector('[data-clear]')).focus()
      this.rows.querySelectorAll('[data-quantity]').forEach(input => {
        const variant = this.byId(input.dataset.quantity)
        const amount = this.chosen.get(variant.id) || 0
        input.value = amount
        input.disabled = Boolean(this.busy)
        input.previousElementSibling.disabled = Boolean(this.busy) || amount === 0
        input.nextElementSibling.disabled = Boolean(this.busy) || this.normalize(variant, amount ? amount + variant.step : variant.min) <= amount
      })
      this.querySelector('[data-selection]').hidden = !count
      this.addButton.disabled = !count || Boolean(this.busy) || Boolean(this.uncertain)
      this.addButton.textContent = this.busy ? this.text.adding : (count ? this.fill(count === 1 ? this.text.addOne : this.text.add, { count, total: this.money(total) }) : this.text.choose)
      this.count = count
    }

    async addToCart() {
      if (this.busy || this.uncertain || !this.chosen.size) return
      this.busy = true
      this.status.textContent = ''
      this.querySelector('[data-cart]').hidden = true
      this.update()
      const root = window.Shopify?.routes?.root || '/'
      let submitted = false
      try {
        const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
        if (!cartResponse.ok) throw new Error(this.text.retry)
        const cart = await cartResponse.json()
        for (const [id, quantity] of this.chosen) {
          const variant = this.byId(id)
          const existing = cart.items.filter(item => String(item.variant_id) === String(id)).reduce((sum, item) => sum + item.quantity, 0)
          if (quantity + existing > this.limit(variant)) throw new Error(this.fill(this.text.limit, { variant: variant.options.join(' · '), count: Math.max(0, this.limit(variant) - existing) }))
        }
        const count = this.count
        const items = Array.from(this.chosen, ([id, quantity]) => ({ id, quantity }))
        submitted = true
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: 'cart-ajax', sections_url: root + 'cart' })
        })
        const result = await response.json()
        if (!response.ok || result.status) throw new Error(result.description || this.text.failed)
        this.chosen.clear()
        this.status.textContent = this.fill(this.text.success, { count })
        this.querySelector('[data-cart]').hidden = false
        // Trigger cart recovery even when the bundled cart section is null.
        this.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          bubbles: true, detail: { product: result, addToCartBtn: this.addButton }
        }))
      } catch (error) {
        this.uncertain = submitted
        this.status.textContent = submitted ? `${error.message} ${this.text.review}` : (error.message || this.text.retry)
        this.querySelector('[data-cart]').hidden = false
      } finally {
        this.busy = false
        this.update()
      }
    }
  })
}


